
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import { useUI } from '../contexts/UIProvider';
import { useLanguage } from '../contexts/LanguageProvider';
import type { User, MessagingPreference, ChurnReason } from '../types';
import SettingsIcon from './icons/SettingsIcon';
import LockClosedIcon from './icons/LockClosedIcon';
import UsersIcon from './icons/UsersIcon';
import SparklesIcon from './icons/SparklesIcon';
import UserMultiSelect from './UserMultiSelect';
import ChurnSurveyModal from './ChurnSurveyModal';
import * as api from '../services/apiService';

const SettingsPage: React.FC = () => {
    const { networkTab } = useNavigation();
    const { t } = useLanguage();
    
    // Determine the initial tab based on navigation payload, defaulting to preferences
    const initialTab = (networkTab === 'security') 
        ? 'security' 
        : 'preferences';

    const [activeTab, setActiveTab] = useState<'preferences' | 'security'>(initialTab);

    useEffect(() => {
        if (networkTab === 'preferences' || networkTab === 'security') {
            setActiveTab(networkTab);
        }
    }, [networkTab]);

    return (
        <div className="space-y-6 font-urbanist animate-in fade-in duration-500 pb-20">
            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                <div className="flex items-center space-x-3">
                    <SettingsIcon className="h-7 w-7 text-brand-orange" />
                    <h2 className="text-3xl font-bold text-white tracking-tight uppercase">{t('SETTINGS')}</h2>
                </div>
                <p className="text-brand-muted">{t('SETTINGS_SUBTITLE')}</p>
            </div>
            
            <div className="bg-brand-surface rounded-lg p-6 space-y-6 border border-gray-800 shadow-sm min-h-[400px]">
                <div className="border-b border-gray-700">
                    <nav className="-mb-px flex space-x-6 overflow-x-auto custom-scrollbar" aria-label="Tabs">
                        <TabButton label={t('PREFERENCES')} isActive={activeTab === 'preferences'} onClick={() => setActiveTab('preferences')} />
                        <TabButton label={t('SECURITY')} isActive={activeTab === 'security'} onClick={() => setActiveTab('security')} />
                    </nav>
                </div>
                
                <div className="pt-4">
                    {activeTab === 'preferences' && <PreferencesContent />}
                    {activeTab === 'security' && <SecurityContent />}
                </div>
            </div>
        </div>
    );
};

const TabButton: React.FC<{ label: string; isActive: boolean; onClick: () => void }> = ({ label, isActive, onClick }) => (
    <button
        onClick={onClick}
        className={`whitespace-nowrap py-3 px-1 border-b-2 font-bold text-sm transition-colors uppercase tracking-widest ${
            isActive
            ? 'border-brand-orange text-brand-orange'
            : 'border-transparent text-brand-muted hover:text-white hover:border-gray-500'
        }`}
    >
        {label}
    </button>
);

const PreferencesContent: React.FC = () => {
    const { currentUser, handleUpdateProfile, users } = useAuth();
    const { handleNavigation } = useNavigation();
    const { showToast } = useUI();
    const { t } = useLanguage();
    const [formData, setFormData] = useState<User | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    

    useEffect(() => {
        setFormData(currentUser);
    }, [currentUser]);
    
    const tier = useMemo(() => currentUser?.subscriptionTier, [currentUser]);
    const isProPlus = tier?.includes('_pro_plus');
    const canManageTeam = tier?.includes('_pro');

    if (!currentUser || !formData) return null;

    const handlePreferenceChange = (
        category: 'notifications' | 'visibility' | 'messaging',
        key: string,
        value: boolean | string
    ) => {
        setFormData(prev => {
            if (!prev) return null;
            if (category === 'messaging') {
                 return {
                    ...prev,
                    preferences: {
                        ...prev.preferences,
                        messaging: key as MessagingPreference,
                    },
                 }
            }
            return {
                ...prev,
                preferences: {
                    ...prev.preferences,
                    [category]: {
                        ...prev.preferences?.[category],
                        [key]: value,
                    },
                },
            };
        });
    };

    const handleCollaboratorsChange = (collaborators: string[]) => {
         setFormData(prev => prev ? ({ ...prev, collaborators }) : null);
    };

    const handleSave = async () => {
        if (!formData) return;
        setIsSaving(true);
        try {
            await handleUpdateProfile(formData);
            showToast('Preferences saved.');
        } catch (error: any) {
            showToast(`Error: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };
    
    return (
        <div className="space-y-6 animate-in fade-in duration-300">
             <SettingsSection title={t('MSG_PREF')}>
                <p className="text-sm text-brand-muted -mt-2">Control who is allowed to send you direct messages.</p>
                {!isProPlus && (
                     <div className="text-center p-4 bg-gray-900/50 rounded-md">
                        <p className="text-sm text-brand-muted mb-2 font-bold uppercase tracking-tight">Upgrade to Pro+ to unlock advanced inbox control.</p>
                        <button type="button" onClick={() => handleNavigation('pumpUpTheVolume')} className="font-bold text-brand-orange hover:underline flex items-center justify-center mx-auto text-xs uppercase tracking-widest">
                            <SparklesIcon className="h-4 w-4 mr-1.5" />
                            View Pro+ Plans
                        </button>
                    </div>
                )}
                <div className="space-y-3">
                    <RadioOption label={t('ALLOW_ALL_MSG')} value="all" checked={formData.preferences?.messaging === 'all' || !isProPlus} onChange={(val) => handlePreferenceChange('messaging', val, true)} disabled={!isProPlus} />
                    <RadioOption label={t('ALLOW_PRO_NET')} value="pro_network" checked={formData.preferences?.messaging === 'pro_network'} onChange={(val) => handlePreferenceChange('messaging', val, true)} disabled={!isProPlus} />
                    <RadioOption label={t('ALLOW_PRO_ONLY')} value="pro_only" checked={formData.preferences?.messaging === 'pro_only'} onChange={(val) => handlePreferenceChange('messaging', val, true)} disabled={!isProPlus} isProFeature={true} />
                    <RadioOption label={t('ALLOW_NET_ONLY')} value="network_only" checked={formData.preferences?.messaging === 'network_only'} onChange={(val) => handlePreferenceChange('messaging', val, true)} disabled={!isProPlus} isProFeature={true} />
                </div>
            </SettingsSection>

             <SettingsSection title={t('NOTIF_PREF')}>
                <Toggle label={t('NOTIF_NEW_MSG')} description="Notify me when I receive a new private message." value={formData.preferences?.notifications?.messages ?? true} onChange={(val) => handlePreferenceChange('notifications', 'messages', val)} />
                <Toggle label={t('NOTIF_ENDORSE')} description="Notify me when someone endorses my profile." value={formData.preferences?.notifications?.endorsements ?? true} onChange={(val) => handlePreferenceChange('notifications', 'endorsements', val)} />
                <Toggle label={t('NOTIF_COMMENTS')} description="Notify me when someone comments on your opportunities." value={formData.preferences?.notifications?.comments ?? true} onChange={(val) => handlePreferenceChange('notifications', 'comments', val)} />
                <Toggle label={t('NOTIF_APPS')} description="Notify me when someone applies to your opportunities." value={formData.preferences?.notifications?.applications ?? true} onChange={(val) => handlePreferenceChange('notifications', 'applications', val)} />
                <Toggle label={t('NOTIF_STATUS')} description="Notify me when the status of your submitted applications changes." value={formData.preferences?.notifications?.statusChanges ?? true} onChange={(val) => handlePreferenceChange('notifications', 'statusChanges', val)} />
                <Toggle label={t('NOTIF_MATCHES')} description="Notify me about new opportunities that match your professional skills." value={formData.preferences?.notifications?.matches ?? true} onChange={(val) => handlePreferenceChange('notifications', 'matches', val)} />
            </SettingsSection>
            
            {canManageTeam && (
                <TeamManagementSection
                    tier={tier!}
                    collaborators={formData.collaborators || []}
                    onCollaboratorsChange={handleCollaboratorsChange}
                    allUsers={users}
                    currentUserHandle={currentUser.handle}
                    showToast={showToast}
                />
            )}

            <SettingsSection title={t('PRIVACY_VISIBILITY')}>
                <div className="space-y-4">
                    <Toggle 
                        label={t('STEALTH_MODE')} 
                        description={t('STEALTH_DESC')} 
                        value={formData.preferences?.visibility?.stealthMode ?? false} 
                        onChange={(val) => handlePreferenceChange('visibility', 'stealthMode', val)} 
                        disabled={!isProPlus}
                        isProFeature={true}
                        onUpgradeClick={() => handleNavigation('pumpUpTheVolume')}
                    />
                    
                    {formData.preferences?.visibility?.stealthMode && isProPlus && (
                        <div className="ml-8 space-y-4 border-l-2 border-brand-orange/30 pl-4 animate-in slide-in-from-left-2 duration-300">
                             <Toggle 
                                label="Hide from Searches" 
                                description="Disappear completely from Global Explorer and Search Results." 
                                value={formData.preferences?.visibility?.hideFromSearch ?? false} 
                                onChange={(val) => handlePreferenceChange('visibility', 'hideFromSearch', val)} 
                            />
                             <Toggle 
                                label="Restricted Profile" 
                                description="Only users in your Endorsement network can view your full bio and details." 
                                value={formData.preferences?.visibility?.restrictedProfile ?? false} 
                                onChange={(val) => handlePreferenceChange('visibility', 'restrictedProfile', val)} 
                            />
                        </div>
                    )}
                </div>
            </SettingsSection>

            <div className="p-4 flex justify-end items-center space-x-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <button onClick={handleSave} disabled={isSaving} className="bg-brand-orange text-white font-bold px-8 py-2 rounded-full hover:opacity-90 disabled:opacity-50 uppercase tracking-widest text-xs shadow-lg">
                        {isSaving ? 'Saving...' : 'Save Preferences'}
                </button>
            </div>

        </div>
    )
}

const validatePassword = (pwd: string) => ({
    minLength: pwd.length >= 8,
    hasUpper: /[A-Z]/.test(pwd),
    hasNumber: /[0-9]/.test(pwd),
    hasSpecial: /[^A-Za-z0-9]/.test(pwd),
});

const SecurityContent: React.FC = () => {
    const { currentUser, handleUpdateProfile, handleChangePassword, logout } = useAuth();
    const { showToast } = useUI();
    const { t } = useLanguage();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [newHandle, setNewHandle] = useState(currentUser?.handle || '');
    const [isHandleLoading, setIsHandleLoading] = useState(false);
    const [isPassLoading, setIsPassLoading] = useState(false);
    const [passError, setPassError] = useState('');
    const [showSurvey, setShowSurvey] = useState(false);
    const [actionType, setActionType] = useState<'freeze' | 'delete'>('freeze');
    const [isChurning, setIsChurning] = useState(false);

    const pwdCriteria = validatePassword(newPassword);
    const isPwdValid = Object.values(pwdCriteria).every(Boolean);

    const handleTriggerChurn = (type: 'freeze' | 'delete') => {
        setActionType(type);
        setShowSurvey(true);
    };

    const handleSubmitChurn = async (reason: ChurnReason) => {
        setIsChurning(true);
        try {
            if (actionType === 'freeze') {
                await api.freezeAccount(reason);
                showToast("Account frozen successfully.");
            } else {
                await api.deleteAccount(reason);
                showToast("Account deleted successfully.");
            }
            logout();
        } catch (error) {
            showToast("Failed to process request. Please contact support.");
            setIsChurning(false);
            setShowSurvey(false);
        }
    };

    const handleHandleChangeSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newHandle || newHandle === currentUser?.handle) return;
        
        setIsHandleLoading(true);
        try {
            await handleUpdateProfile({ ...currentUser!, handle: newHandle });
            showToast('Handle updated successfully!');
        } catch (error: any) {
            showToast(`Error: ${error.message}`);
        } finally {
            setIsHandleLoading(false);
        }
    };

    const handlePassSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setPassError('');

        if (!isPwdValid) {
            setPassError('Password does not meet the required criteria.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPassError('Passwords do not match.');
            return;
        }

        setIsPassLoading(true);
        const result = await handleChangePassword(currentPassword, newPassword);
        setIsPassLoading(false);

        if (result.error) {
            setPassError(result.error);
        } else {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        }
    };

    return (
        <div className="space-y-10 animate-in fade-in duration-300">
            <SettingsSection title="Profile Identity">
                <form onSubmit={handleHandleChangeSubmit} className="space-y-4 max-w-md">
                    <div>
                        <label className="block text-sm font-medium text-brand-muted mb-1">{t('UPDATE_HANDLE')}</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-orange font-bold">@</span>
                            <input 
                                type="text" 
                                value={newHandle} 
                                onChange={e => setNewHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} 
                                className="w-full bg-gray-700 text-white rounded-md p-3 pl-8 focus:outline-none focus:ring-2 focus:ring-brand-orange font-mono font-bold"
                            />
                        </div>
                        <p className="text-xs text-brand-muted mt-2">Changing your handle updates your profile URL and all @mentions.</p>
                    </div>
                    <div className="flex justify-end">
                        <button 
                            type="submit" 
                            disabled={isHandleLoading || newHandle === currentUser?.handle} 
                            className="bg-brand-orange text-white font-bold px-8 py-2 rounded-full hover:opacity-90 disabled:opacity-50 transition-opacity uppercase tracking-widest text-xs shadow-lg"
                        >
                            {isHandleLoading ? 'Updating...' : 'Change Handle'}
                        </button>
                    </div>
                </form>
            </SettingsSection>

            <SettingsSection title={t('SECURITY_CREDENTIALS')}>
                <form onSubmit={handlePassSubmit} className="space-y-4 max-w-md">
                    <div>
                        <label className="block text-sm font-medium text-brand-muted mb-1 font-bold">{t('CURRENT_PASSWORD')}</label>
                        <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange font-bold"/>
                    </div>
                     <div>
                        <label className="block text-sm font-medium text-brand-muted mb-1 font-bold">{t('NEW_PASSWORD')}</label>
                        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange font-bold"/>
                        {newPassword.length > 0 && (
                            <ul className="mt-2 space-y-1">
                                {[
                                    { ok: pwdCriteria.minLength, label: 'At least 8 characters' },
                                    { ok: pwdCriteria.hasUpper,  label: 'At least 1 uppercase letter' },
                                    { ok: pwdCriteria.hasNumber, label: 'At least 1 number' },
                                    { ok: pwdCriteria.hasSpecial,label: 'At least 1 special character' },
                                ].map(({ ok, label }) => (
                                    <li key={label} className={`text-xs flex items-center gap-1.5 ${ok ? 'text-green-400' : 'text-brand-muted'}`}>
                                        <span>{ok ? '✓' : '○'}</span>{label}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                     <div>
                        <label className="block text-sm font-medium text-brand-muted mb-1 font-bold">{t('CONFIRM_PASSWORD')}</label>
                        <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange font-bold"/>
                    </div>
                    {passError && <p className="text-sm text-red-400 text-center font-bold">{passError}</p>}
                    <div className="pt-2 flex justify-end">
                         <button type="submit" disabled={isPassLoading} className="flex items-center space-x-2 bg-brand-orange text-white font-bold px-8 py-2 rounded-full hover:opacity-90 disabled:opacity-50 uppercase tracking-widest text-xs shadow-lg">
                            {isPassLoading ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                                <LockClosedIcon className="h-5 w-5" />
                            )}
                            <span>Update Password</span>
                        </button>
                    </div>
                </form>
            </SettingsSection>

            <SettingsSection title={t('ACCOUNT_MGMT')}>
                <div className="bg-red-500/5 border border-red-500/20 p-6 rounded-lg space-y-4">
                    <div>
                        <h4 className="font-bold text-red-400 mb-1 uppercase tracking-tight">{t('DANGER_ZONE')}</h4>
                        <p className="text-sm text-brand-muted">Sensitive actions that affect your data and profile availability.</p>
                    </div>
                    <div className="flex flex-col md:flex-row gap-4 pt-2">
                        <button
                            onClick={() => handleTriggerChurn('freeze')}
                            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition-colors uppercase tracking-widest text-xs"
                        >
                            {t('FREEZE_ACCOUNT')}
                        </button>
                        <button
                            onClick={() => handleTriggerChurn('delete')}
                            className="flex-1 bg-red-600/10 hover:bg-red-600 text-red-600 hover:text-white border border-red-600 font-bold py-3 px-6 rounded-lg transition-all uppercase tracking-widest text-xs"
                        >
                            {t('DELETE_ACCOUNT')}
                        </button>
                    </div>
                </div>
            </SettingsSection>

            {showSurvey && (
                <ChurnSurveyModal
                    actionType={actionType}
                    onClose={() => setShowSurvey(false)}
                    onSubmit={handleSubmitChurn}
                    isProcessing={isChurning}
                />
            )}
        </div>
    );
};

const TeamManagementSection: React.FC<{
    tier: User['subscriptionTier'],
    collaborators: string[],
    onCollaboratorsChange: (collaborators: string[]) => void,
    allUsers: User[],
    currentUserHandle: string,
    showToast: (message: string) => void,
}> = ({ tier, collaborators, onCollaboratorsChange, allUsers, currentUserHandle, showToast }) => {
    const { t } = useLanguage();
    const limit = useMemo(() => {
        if (tier?.includes('talent_pro') || tier?.includes('business_pro')) return tier.includes('_plus') ? 2 : 1;
        return 0;
    }, [tier]);

    const selectedUsers = useMemo(() =>
        collaborators.map(handle => allUsers.find(u => u.handle === handle)).filter((u): u is User => !!u)
    , [collaborators, allUsers]);

    const availableUsers = useMemo(() =>
        allUsers.filter(u => u.handle !== currentUserHandle)
    , [allUsers, currentUserHandle]);

    const handleSelectionChange = (newSelectedUsers: User[]) => {
        if (newSelectedUsers.length > limit) {
            showToast(`Limit reached: Your plan allows for ${limit} collaborators.`);
            return;
        }
        onCollaboratorsChange(newSelectedUsers.map(u => u.handle));
    };

    return (
        <SettingsSection title={t('TEAM_ACCESS')}>
             <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <div className="flex items-center space-x-3">
                    <UsersIcon className="h-6 w-6 text-brand-orange"/>
                    <div>
                        <h4 className="font-bold text-white uppercase tracking-tight">{t('COLLABORATORS')} ({collaborators.length}/{limit})</h4>
                        <p className="text-xs text-brand-muted">Grant management access to trusted members of your team.</p>
                    </div>
                </div>

                <div className="mt-4">
                    <UserMultiSelect
                        label=""
                        placeholder="Search for team members..."
                        availableUsers={availableUsers}
                        selectedUsers={selectedUsers}
                        onSelectionChange={handleSelectionChange}
                    />
                </div>
            </div>
        </SettingsSection>
    )
};

const SettingsSection: React.FC<{ title: string, children: React.ReactNode }> = ({ title, children }) => (
    <div className="mb-8 last:mb-0">
        <h3 className="text-xl font-bold text-white border-b border-gray-700 pb-2 mb-4 tracking-tight uppercase">{title}</h3>
        <div className="space-y-4">{children}</div>
    </div>
);

const RadioOption: React.FC<{
    label: string;
    value: string;
    checked: boolean;
    onChange: (value: string) => void;
    disabled: boolean;
    isProFeature?: boolean;
}> = ({ label, value, checked, onChange, disabled, isProFeature }) => (
    <label className={`flex items-center p-3 rounded-lg transition-colors ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-800/50'} ${checked ? 'bg-brand-orange/10 border-brand-orange/30 border' : 'border border-transparent'}`}>
        <input 
            type="radio" 
            name="messaging" 
            value={value}
            checked={checked}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className="h-4 w-4 bg-gray-700 border-gray-600 text-brand-orange focus:ring-brand-orange focus:ring-2"
        />
        <span className="ml-3 text-sm font-bold text-white">{label}</span>
        {isProFeature && (
            <span className="ml-2 bg-yellow-500/20 text-yellow-300 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">PRO+</span>
        )}
    </label>
);

const Toggle: React.FC<{
    label: string, 
    description: string, 
    value: boolean, 
    onChange: (value: boolean) => void, 
    disabled?: boolean,
    isProFeature?: boolean,
    onUpgradeClick?: () => void
}> = ({ label, description, value, onChange, disabled, isProFeature, onUpgradeClick }) => (
    <div className="flex items-start justify-between p-2 rounded-lg hover:bg-gray-800/20 transition-colors">
        <div>
            <div className="flex items-center space-x-2">
                <p className={`font-bold text-white uppercase tracking-tight ${disabled ? 'opacity-50' : ''}`}>{label}</p>
                {disabled && isProFeature && (
                    <span className="bg-yellow-500/20 text-yellow-300 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">PRO+</span>
                )}
            </div>
            <p className={`text-sm text-brand-muted font-medium ${disabled ? 'opacity-50' : ''}`}>{description}</p>
            {disabled && isProFeature && onUpgradeClick && (
                <button onClick={onUpgradeClick} className="text-xs font-bold text-brand-orange hover:underline mt-1 uppercase tracking-widest">
                    Upgrade to Unlock
                </button>
            )}
        </div>
        <button
            type="button"
            onClick={() => !disabled && onChange(!value)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 focus:ring-offset-brand-surface ${value ? 'bg-brand-orange' : 'bg-gray-600'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            role="switch"
            aria-checked={value}
            disabled={disabled}
        >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${value ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
    </div>
);

export default SettingsPage;
